import { useState } from 'react';
import { Button, Chip, IconButton, Stack, Tooltip } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import PersonOffIcon from '@mui/icons-material/PersonOff';
import { type GridColDef } from '@mui/x-data-grid';
import { useTranslation } from 'react-i18next';
import { PageHeader } from '../../../shared/components/PageHeader';
import { DataTable, type DataTableQuery } from '../../../shared/components/DataTable';
import { GRID_PAGE_SIZE, usePersistedGridState } from '../../../shared/grid/usePersistedGridState';
import { IncludeInactiveToggle } from '../../../shared/components/IncludeInactiveToggle';
import { ConfirmDialog } from '../../../shared/components/ConfirmDialog';
import { ErrorState } from '../../../shared/components/ErrorState';
import { useToast } from '../../../shared/stores/toast.store';
import { describeApiError } from '../../../shared/api/errors';
import { formatCpf } from '../../../shared/utils/document';
import { employeesHooks } from '../hooks';
import { employeesApi } from '../api';
import { EmployeeFormDialog } from '../EmployeeFormDialog';
import type { EmployeeDto, EmployeeListItem, StatusLogin } from '../types';

const DEFAULT_QUERY: DataTableQuery = {
  page: 1,
  pageSize: GRID_PAGE_SIZE,
  sortBy: 'nome',
  sortOrder: 'asc',
};

function statusLoginChipColor(s: StatusLogin): 'success' | 'error' | 'default' {
  return s === 'ATV' ? 'success' : s === 'BLQ' ? 'error' : 'default';
}

export function EmployeesListPage(): JSX.Element {
  const { t } = useTranslation();
  const toast = useToast();
  const { query, setQuery, clear } = usePersistedGridState('colaboradores', DEFAULT_QUERY, {});
  const [editing, setEditing] = useState<EmployeeDto | null | 'new'>(null);
  const [pendingDelete, setPendingDelete] = useState<EmployeeListItem | null>(null);

  const listQuery = employeesHooks.useList(query);
  const removeMutation = employeesHooks.useDelete();

  async function openEdit(row: EmployeeListItem): Promise<void> {
    try {
      setEditing(await employeesApi.get(row.id));
    } catch (err) {
      toast.error(describeApiError(err));
    }
  }

  async function confirmDelete(): Promise<void> {
    if (!pendingDelete) return;
    try {
      await removeMutation.mutateAsync(pendingDelete.id);
      toast.success(t('features.colaboradores.toasts.deleted'));
      setPendingDelete(null);
    } catch (err) {
      toast.error(describeApiError(err));
    }
  }

  const columns: GridColDef<EmployeeListItem>[] = [
    { field: 'nome', headerName: t('features.colaboradores.columns.nome'), flex: 1, minWidth: 200 },
    {
      field: 'cpf',
      headerName: t('features.colaboradores.columns.cpf'),
      width: 150,
      valueFormatter: (value: string) => formatCpf(value),
    },
    {
      field: 'tipoContrato',
      headerName: t('features.colaboradores.columns.tipoContrato'),
      width: 90,
      sortable: false,
      renderCell: (params) => (
        <Chip
          label={params.value}
          size="small"
          color={params.value === 'PJ' ? 'primary' : params.value === 'TMP' ? 'warning' : 'default'}
          variant="outlined"
        />
      ),
    },
    {
      field: 'cargoNome',
      headerName: t('features.colaboradores.columns.cargoNome'),
      width: 160,
      sortable: false,
    },
    {
      field: 'email',
      headerName: t('features.colaboradores.columns.email'),
      width: 200,
      sortable: false,
    },
    {
      field: 'statusLogin',
      headerName: t('features.colaboradores.columns.statusLogin'),
      width: 110,
      sortable: false,
      renderCell: (params) => {
        const s = params.value as StatusLogin;
        return (
          <Chip
            label={t(`features.colaboradores.statusLogin.${s}`)}
            size="small"
            color={statusLoginChipColor(s)}
            variant={s === 'DES' ? 'outlined' : 'filled'}
          />
        );
      },
    },
    {
      field: 'ativo',
      headerName: t('features.colaboradores.columns.ativo'),
      width: 110,
      sortable: false,
      renderCell: (params) => (
        <Chip
          label={
            params.value
              ? t('features.colaboradores.status.active')
              : t('features.colaboradores.status.inactive')
          }
          color={params.value ? 'success' : 'default'}
          size="small"
          variant={params.value ? 'filled' : 'outlined'}
        />
      ),
    },
    {
      field: 'actions',
      headerName: '',
      width: 100,
      sortable: false,
      filterable: false,
      align: 'right',
      headerAlign: 'right',
      renderCell: (params) => (
        <Stack direction="row" spacing={0.5}>
          <Tooltip title={t('common.edit')}>
            <IconButton
              size="small"
              aria-label={t('features.colaboradores.actions.edit', { nome: params.row.nome })}
              onClick={() => void openEdit(params.row)}
            >
              <EditIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title={t('features.colaboradores.actions.delete', { nome: params.row.nome })}>
            <span>
              <IconButton
                size="small"
                color="error"
                disabled={!params.row.ativo}
                aria-label={t('features.colaboradores.actions.delete', { nome: params.row.nome })}
                onClick={() => setPendingDelete(params.row)}
              >
                <PersonOffIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        </Stack>
      ),
    },
  ];

  if (listQuery.isLoadingError) {
    return (
      <>
        <PageHeader title={t('menu.employeesRegistry')} />
        <ErrorState
          description={describeApiError(listQuery.error)}
          onRetry={() => listQuery.refetch()}
        />
      </>
    );
  }

  // Cadastro em tela cheia dentro da área de conteúdo (substitui a lista; o
  // estado da lista — query/filtros — é preservado pois a página segue montada).
  if (editing !== null) {
    return (
      <EmployeeFormDialog
        open
        employee={editing === 'new' ? null : editing}
        onClose={() => setEditing(null)}
      />
    );
  }

  return (
    <>
      <PageHeader
        title={t('menu.employeesRegistry')}
        breadcrumbs={[{ label: t('menu.employees') }, { label: t('menu.employeesRegistry') }]}
        actions={
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => setEditing('new')}>
            {t('features.colaboradores.actions.new')}
          </Button>
        }
      />

      <DataTable<EmployeeListItem>
        rows={listQuery.data?.data ?? []}
        columns={columns}
        total={listQuery.data?.meta.total ?? 0}
        loading={listQuery.isLoading || listQuery.isFetching}
        query={query}
        onQueryChange={setQuery}
        onClear={clear}
        searchPlaceholder={t('features.colaboradores.searchPlaceholder')}
        filters={<IncludeInactiveToggle query={query} onChange={setQuery} />}
      />

      <ConfirmDialog
        open={pendingDelete !== null}
        title={t('features.colaboradores.deleteConfirm.title')}
        description={t('features.colaboradores.deleteConfirm.description', {
          nome: pendingDelete?.nome ?? '',
        })}
        confirmLabel={t('features.colaboradores.actions.delete', {
          nome: pendingDelete?.nome ?? '',
        })}
        loading={removeMutation.isPending}
        onConfirm={confirmDelete}
        onCancel={() => setPendingDelete(null)}
      />
    </>
  );
}
